import React, { useEffect, useState } from "react";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { fetchStudentProfile } from "api/student/v2/popup.api";
import { getFlagByCountryCode, getStudentNickname } from "helpers/String.helper";
import { IUserStudent } from "api/interfaces/user.interface";
import { IDiary } from "api/interfaces/profile.interface";
import { usePopup } from "contexts/PopupContext";
import StudentDiaryDetailPopup from "./StudentDiaryDetailPopup";
import { addFollow, cancelFollow } from "api/student/Follow.api";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

interface StudentProfilePopupProps {
    userStudentId: number;
    onClose?: () => void;
}

const StudentProfilePopup: React.FC<StudentProfilePopupProps> = ({ userStudentId, onClose }) => {
    const { showPopup } = usePopup();
    const navigate = useNavigate();
    const { info: myInfo } = useSelector((state: any) => state.user);
    const [userStudent, setUserStudent] = useState<IUserStudent | null>(null);
    const [diaryList, setDiaryList] = useState<IDiary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFollowLoading, setIsFollowLoading] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);
    const [followerCount, setFollowerCount] = useState(0);
    const [favoriteByAgencyCount, setFavoriteByAgencyCount] = useState(0);

    // 내 프로필인지 확인
    const isMyProfile = userStudent?.user?.id === myInfo?.id;

    // 나이 계산 함수
    const calculateAge = (birthday: string | null): number | null => {
        if (!birthday) return null;
        const today = new Date();
        const birthDate = new Date(birthday);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // 스크롤 이벤트 핸들러 - 현재 이미지 인덱스 업데이트
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const container = e.currentTarget;
        const scrollLeft = container.scrollLeft;
        const containerWidth = container.clientWidth;
        const newIndex = Math.round(scrollLeft / containerWidth);
        setCurrentImageIndex(newIndex);
    };

    // 팔로우 토글 함수
    const handleFollowToggle = async () => {
        if (!userStudent || isFollowLoading) return;

        try {
            setIsFollowLoading(true);

            if (userStudent.isFollowing) {
                // 팔로우 취소
                await cancelFollow(userStudentId);
            } else {
                // 팔로우 추가
                await addFollow(userStudentId);
            }

            // 상태 업데이트
            setUserStudent(prev => prev ? { ...prev, isFollowing: !prev.isFollowing } : null);
        } catch (error) {
            console.error("팔로우 토글 실패:", error);
        } finally {
            setIsFollowLoading(false);
        }
    };

    useEffect(() => {
        fetchStudentProfileData();
    }, [userStudentId]);

    const fetchStudentProfileData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await fetchStudentProfile(userStudentId);
            setUserStudent(result.userStudent);
            setDiaryList(result.diaryList || []);
            setFollowingCount(result.followingCount || 0);
            setFollowerCount(result.followerCount || 0);
            setFavoriteByAgencyCount(result.userStudent.favoriteCount || 0);
        } catch (error) {
            console.error("지망생 프로필 조회 실패:", error);
            setError(getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtLoadFailed") || "지망생 정보를 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    // 로딩 상태
    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
                <div className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col relative overflow-y-hidden">
                    <div className="animate-pulse">
                        {/* 닫기 버튼 스켈레톤 */}
                        <div className="absolute top-4 right-4 w-8 h-8 bg-gray-300 rounded-full z-10"></div>

                        {/* 스크롤 가능한 전체 콘텐츠 영역 스켈레톤 */}
                        <div className="flex-1 overflow-y-auto">
                            {/* 프로필 사진 + 추가사진 갤러리 영역 스켈레톤 - 스와이프 가능 */}
                            <div className="relative">
                                <div className="w-full aspect-square rounded-t-2xl overflow-hidden relative">
                                    {/* 이미지 갤러리 컨테이너 스켈레톤 */}
                                    <div className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                                        {/* 프로필 사진 스켈레톤 (첫 번째 슬라이드) */}
                                        <div className="flex-shrink-0 w-full h-full snap-center relative bg-gray-200">
                                        </div>

                                        {/* 추가 사진 스켈레톤들 (나머지 슬라이드들) */}
                                        {[...Array(3)].map((_, index) => (
                                            <div key={index} className="flex-shrink-0 w-full h-full snap-center relative bg-gray-200">
                                            </div>
                                        ))}
                                    </div>

                                    {/* 하단 그라데이션 오버레이 스켈레톤 - 모든 사진에서 표시 */}
                                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-300/70 via-gray-200/30 to-transparent"></div>

                                    {/* 프로필 정보 스켈레톤 - 모든 사진에서 표시 */}
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="flex justify-between items-end">
                                            {/* 좌측: 나이, 이름, 국적 스켈레톤 */}
                                            <div>
                                                <div className="h-4 bg-white/30 rounded w-8 mb-2"></div>
                                                <div className="flex items-center gap-2">
                                                    <div className="h-5 bg-white/30 rounded w-20"></div>
                                                    <div className="h-5 bg-white/30 rounded w-6"></div>
                                                </div>
                                            </div>

                                            {/* 우측: 키, 몸무게 스켈레톤 */}
                                            <div className="text-right">
                                                <div className="h-4 bg-white/30 rounded w-16"></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 현재 이미지 번호 / 총 이미지 수 스켈레톤 */}
                                    <div className="absolute top-4 left-4 bg-gray-300/50 text-white px-2 py-1 rounded text-sm w-12 h-6"></div>
                                </div>
                            </div>

                            {/* 지망생 프로필 내용 스켈레톤 */}
                            <div className="p-6 pb-20">
                                {/* 팔로우/팔로워 정보 스켈레톤 */}
                                <div className="mb-6">
                                    <div className="flex justify-center gap-8">
                                        <div className="text-center">
                                            <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                                            <div className="h-6 bg-gray-200 rounded w-8"></div>
                                        </div>
                                        <div className="text-center">
                                            <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                                            <div className="h-6 bg-gray-200 rounded w-8"></div>
                                        </div>
                                        <div className="text-center">
                                            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                                            <div className="h-6 bg-gray-200 rounded w-8"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* 다이어리 목록 스켈레톤 */}
                                <div className="mb-6">
                                    <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
                                    <div className="space-y-1">
                                        {[...Array(3)].map((_, index) => (
                                            <div key={index} className="flex items-start justify-between p-2 bg-gray-50 rounded-lg">
                                                <div className="flex-1">
                                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="flex items-center space-x-1">
                                                            <div className="w-3 h-3 bg-gray-200 rounded"></div>
                                                            <div className="h-3 bg-gray-200 rounded w-4"></div>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <div className="w-3 h-3 bg-gray-200 rounded"></div>
                                                            <div className="h-3 bg-gray-200 rounded w-4"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="ml-2 h-3 bg-gray-200 rounded w-12"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 하단 버튼 영역 스켈레톤 - 팝업 하단에 고정 */}
                        <div className="bg-white border-t border-gray-200 p-4 rounded-b-2xl">
                            <div className="flex gap-3">
                                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                                <div className="flex-1 h-12 bg-gray-200 rounded-lg"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error || !userStudent) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-6">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtLoadFailedTitle") || "지망생 정보 로드 실패"}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {error || getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtLoadFailedMessage") || "지망생 정보를 불러올 수 없습니다."}
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            {getLanguageValue(PAGE_TYPE.COMMON, "btn.txtClose") || "닫기"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col relative">
                {/* 닫기 버튼 - 팝업 내부 상단 우측에 고정 */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors z-10"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* 스크롤 가능한 콘텐츠 영역 */}
                <div className="flex-1 overflow-y-auto">
                    {/* 프로필 사진 + 추가사진 갤러리 영역 - 스와이프 가능 */}
                    <div className="relative">
                        <div className="w-full aspect-square rounded-t-2xl overflow-hidden relative">
                            {/* 이미지 갤러리 컨테이너 */}
                            <div
                                className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                onScroll={handleScroll}
                            >
                                {/* 프로필 사진 (첫 번째 슬라이드) */}
                                <div className="flex-shrink-0 w-full h-full snap-center relative">
                                    <img
                                        src={getImageUrl(userStudent.profileImage) || "/imgs/dummy/basic-img.png"}
                                        alt={userStudent.nickname || getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtProfileBackground") || "지망생 프로필 배경"}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = "/imgs/dummy/basic-img.png";
                                        }}
                                    />
                                </div>

                                {/* 추가 사진들 (나머지 슬라이드들) */}
                                {userStudent.userStudentImages && userStudent.userStudentImages.map((imageInfo, index) => (
                                    <div
                                        key={imageInfo.id || index}
                                        className="flex-shrink-0 w-full h-full snap-center relative"
                                    >
                                        <img
                                            src={getImageUrl(imageInfo.image)}
                                            alt={`${getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtAdditionalPhoto") || "추가 사진"} ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = "/imgs/dummy/basic-img.png";
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* 하단 그라데이션 오버레이 - 모든 사진에서 표시 */}
                            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                            {/* 프로필 정보 - 모든 사진에서 표시 */}
                            <div className="absolute bottom-4 left-4 right-4 text-white">
                                <div className="flex justify-between items-end">
                                    {/* 좌측: 나이, 이름, 국적 */}
                                    <div>
                                        {userStudent.birthday && (
                                            <p className="text-sm text-white/90 mb-1">
                                                {calculateAge(userStudent.birthday.toString())}세
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-bold">
                                                {getStudentNickname(userStudent, null)}
                                            </h3>
                                            {userStudent.countryCode && (
                                                <span className="text-lg">
                                                    {getFlagByCountryCode(userStudent.countryCode)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 우측: 키, 몸무게 */}
                                    <div className="text-right">
                                        <div className="flex items-center gap-2 text-sm text-white/90">
                                            {userStudent.height && (
                                                <span>{userStudent.height}cm</span>
                                            )}
                                            {userStudent.height && userStudent.weight && (
                                                <span>•</span>
                                            )}
                                            {userStudent.weight && (
                                                <span>{userStudent.weight}kg</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 현재 이미지 번호 / 총 이미지 수 - 동적으로 업데이트 */}
                            <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm">
                                {currentImageIndex + 1} / {(userStudent.userStudentImages?.length || 0) + 1}
                            </div>
                        </div>
                    </div>

                    {/* 지망생 프로필 내용 */}
                    <div className="p-6 pb-20">
                        {/* 팔로우/팔로워 정보 */}
                        {userStudent.isActiveOfPublicProfile && (
                            <div className="mb-6">
                                <div className="flex justify-center gap-8">
                                    <div className="text-center">
                                        <div className="text-gray-600 text-[10px]">{getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtFollowing") || "팔로잉"}</div>
                                        <div className="font-bold text-lg">{followingCount || 0}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-[10px]">{getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtFollower") || "팔로워"}</div>
                                        <div className="font-bold text-lg">{followerCount || 0}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-[10px]">{getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtFavorite") || "즐겨찾기"}</div>
                                        <div className="font-bold text-lg">{favoriteByAgencyCount || 0}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-[10px]">{getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtProfileView") || "프로필 뷰"}</div>
                                        <div className="font-bold text-lg">{userStudent.castingManagerViewCount || 0}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 다이어리 목록 */}
                        {diaryList && diaryList.length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtRecentDiary") || "최근등록된 다이어리"}
                                </h3>
                                <div className="space-y-1">
                                    {diaryList.map((diary) => (
                                        <div
                                            key={diary.id}
                                            className="flex items-start justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                            onClick={() => {
                                                // 다이어리 상세 팝업 표시
                                                showPopup(
                                                    <StudentDiaryDetailPopup
                                                        diaryId={diary.id.toString()}
                                                        onClose={() => {
                                                            // 팝업이 닫힐 때 다이어리 목록 새로고침
                                                            fetchStudentProfileData();
                                                        }}
                                                    />
                                                );
                                            }}
                                        >
                                            <div className="flex-1">
                                                <div
                                                    className="text-sm text-[#CF3688] font-medium mb-1"
                                                    style={{
                                                        wordBreak: 'break-all',
                                                        overflowWrap: 'anywhere',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    {diary.title}
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex items-center space-x-1">
                                                        <img src="/imgs/icons/icon-heart.svg" alt="like" className="w-3 h-3" />
                                                        <span className="text-xs text-gray-500 font-medium">
                                                            {diary.likeCount || 0}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-3 h-3" />
                                                        <span className="text-xs text-gray-500 font-medium">
                                                            {diary.commentCount || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="ml-2 text-xs text-gray-500">
                                                {formatTimeAgo(diary.createdAt)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 하단 버튼 영역 - 팝업 하단에 고정 */}
                <div className="bg-white border-t border-gray-200 p-4 rounded-b-2xl">
                    <div className="flex gap-3">
                        {/* 팔로우 버튼 - 내 프로필이 아닐 때만 표시 */}
                        {!isMyProfile && (
                            <button
                                className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg transition-colors font-medium ${isFollowLoading
                                    ? 'cursor-not-allowed'
                                    : userStudent.isFollowing
                                        ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                        : 'bg-[#CF3688] hover:bg-[#B82F77] text-white'
                                    }`}
                                onClick={handleFollowToggle}
                                disabled={isFollowLoading}
                            >
                                <span className={`${isFollowLoading ? 'opacity-50' : ''}`}>
                                    {userStudent.isFollowing
                                        ? getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtUnfollow") || '팔로우 해제'
                                        : getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtFollow") || '팔로우'
                                    }
                                </span>
                            </button>
                        )}

                        {/* 상세정보 보기 버튼 */}
                        {userStudent.isActiveOfPublicProfile ? (
                            <button
                                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-[#CF3688] hover:bg-[#B82F77] text-white rounded-lg transition-colors font-medium"
                                onClick={() => {
                                    navigate(`/student/wannabe/${userStudentId}`);
                                    if (onClose) onClose();
                                }}
                            >
                                {getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtViewDetail") || "상세정보 보기"}
                            </button>
                        ) :
                            <div className="flex-1 flex items-center justify-center gap-2 py-3 px-4 text-gray-500 border border-gray-200 rounded-lg font-medium">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "studentProfilePopup.txtNotPublicProfile") || "프로필이 공개되지 않았습니다."}
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentProfilePopup;
